import { Component, Inject, Injector, Input, OnInit, output } from '@angular/core';
import { FeatureFlagService, PartnerService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import {
  CustomExportEvent,
  TABLE_DEFINITION,
  VaFilteredMatTableService,
  VaTableSortService,
} from '@vendasta/va-filter2-table';
import { firstValueFrom } from 'rxjs';
import {
  BaseSalesOrdersExportAllService,
  BaseSalesOrdersTableFilterService,
  BaseSalesOrdersTableService,
  OrdersRow,
  RequestedExportType,
  SALES_ORDERS_TABLE_FACTORY_PARAM_TOKEN,
  SalesOrdersTableFactoryParams,
  SalesOrdersTableOptions,
} from './interface';
import { SalesOrdersExportAllService } from './sales-order-export-all-service';
import { CustomerSalesOrdersTableService } from './sales-orders-customer-table.service';
import { CustomerSalesOrdersTableFilterService } from './sales-orders-table-customer-filter.service';
import { SalesOrdersTableFilterService } from './sales-orders-table-filter.service';
import { tableDefinitionFactory } from './sales-orders-table-model';
import { SalesOrdersTableService } from './sales-orders-table.service';
import { SalespersonService } from './salesperson.service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SalesOrdersTableTabFilterService } from './sales-orders-table-tab-filter.service';

@Component({
  selector: 'sales-ui-sales-orders-table',
  templateUrl: './sales-orders-table.component.html',
  styleUrls: ['./sales-orders-table.component.scss'],
  providers: [
    VaTableSortService,
    SalespersonService,
    SalesOrdersTableService,
    CustomerSalesOrdersTableService,
    VaFilteredMatTableService,
    SalesOrdersTableTabFilterService,
    SalesOrdersTableFilterService,
    CustomerSalesOrdersTableFilterService,
    SalesOrdersExportAllService,
    {
      provide: TABLE_DEFINITION,
      useFactory: tableDefinitionFactory,
      deps: [
        SalesOrdersTableFilterService,
        CustomerSalesOrdersTableFilterService,
        SALES_ORDERS_TABLE_FACTORY_PARAM_TOKEN,
        TranslateService,
        FeatureFlagService,
        PartnerService,
      ],
    },
  ],
  standalone: false,
})
export class SalesOrdersTableComponent implements OnInit {
  @Input() options: SalesOrdersTableOptions = {};

  @Input() showStats = false;
  @Input() canAccessSalesTeams = true;
  @Input() set salesOrdersTab(tab: number) {
    if (!this.tableFactoryParams?.inBusinessCenter && this.dataService) {
      this.dataService.salesOrdersTab$$.next(tab);
      this.salesOrdersTableTabFilterService.setTabStatusFilters(tab, this.tableFilterService?.filters);
    }
  }

  tableServicesInitialized = output();

  presetViews: string;

  private tableFilterService: BaseSalesOrdersTableFilterService;
  public dataService: BaseSalesOrdersTableService;
  public exportAllDataService: BaseSalesOrdersExportAllService<OrdersRow>;

  constructor(
    private inj: Injector,
    @Inject(SALES_ORDERS_TABLE_FACTORY_PARAM_TOKEN) private readonly tableFactoryParams: SalesOrdersTableFactoryParams,
    private readonly i18n: TranslateService,
    private readonly productAnalyticsService: ProductAnalyticsService,
    private readonly snackBarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly salesOrdersTableTabFilterService: SalesOrdersTableTabFilterService,
  ) {}

  ngOnInit(): void {
    const forcedFilters = this?.options?.forcedFilters || {};
    const exportOptions = this?.options?.exportOptions || RequestedExportType.Partner;

    this.productAnalyticsService.trackEvent('sales_orders_table_viewed', 'sales_orders', 'viewed');

    if (this.tableFactoryParams?.inBusinessCenter) {
      this.tableFilterService = this.inj.get(CustomerSalesOrdersTableFilterService);
      this.dataService = this.inj.get(CustomerSalesOrdersTableService);
    } else {
      this.tableFilterService = this.inj.get(SalesOrdersTableFilterService);
      this.dataService = this.inj.get(SalesOrdersTableService);
      // TODO initialize this outside of the isCustomerTable logic - all centers should be able to export
      this.exportAllDataService = this.inj.get(SalesOrdersExportAllService);
      this.exportAllDataService.initialize(forcedFilters, exportOptions);
    }

    this.tableFilterService.initialize(forcedFilters, this.canAccessSalesTeams);
    this.dataService.initialize(forcedFilters);
    this.presetViews = this.dataService.presetViews;

    this.tableServicesInitialized.emit();
  }

  async handleCustomExportEvent(event: CustomExportEvent): Promise<void> {
    // check the total to prevent excessively large export processing
    firstValueFrom(this.dataService.totalResults$).then((total) => {
      if (total > 500000) {
        this.snackBarService.openErrorSnack(
          this.translateService.instant('FRONTEND.SALES_UI.EXPORT.ERROR_TOO_MANY_ROWS'),
        );
        return;
      } else if (total === 0) {
        this.snackBarService.openErrorSnack(this.translateService.instant('FRONTEND.SALES_UI.EXPORT.ERROR_NO_RESULTS'));
        return;
      } else {
        this.exportAllDataService.exportAllRowsToCSV(event.requestInfo, event.optionId);
      }
    });
  }
}
